<template>
  <div class="wh" :class="{ 'wh-chrome': showChrome }" :style="frameStyle">
    <div v-if="showChrome" class="wh-head">
      <div class="wh-title">{{ config.title }}</div>
      <div v-if="loading" class="wh-spin" />
    </div>
    <div class="wh-body" :style="{ padding: pad + 'px' }">
      <div v-if="error" class="wh-error">{{ error }}</div>
      <div v-else-if="loading && !ready" class="wh-loading">Loading…</div>
      <template v-else-if="widget">
        <component
          :is="stock"
          v-if="widget.kind === 'stock' && stock"
          :inputs="inputs"
          :config="config"
          :width="bodyWidth"
          :height="bodyHeight"
        />
        <D3Insight
          v-else-if="widget.kind === 'd3'"
          :key="widget.version + ':' + reloads"
          :code="widget.definition.code || ''"
          :inputs="inputRows"
          :min-height="widget.definition.min_height || 120"
          :fixed-height="bodyHeight"
        />
        <iframe
          v-else-if="widget.kind === 'html'"
          class="wh-frame"
          sandbox="allow-scripts"
          referrerpolicy="no-referrer"
          :srcdoc="htmlDocument"
          title="Widget"
        />
        <div v-else class="wh-error">Unknown widget kind {{ widget.kind }}.</div>
      </template>
      <div v-else class="wh-error">Widget {{ placement.widget }} is not available.</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import D3Insight from '../D3Insight.vue';
import { stockComponent } from './registry';
import { TOKENS_CSS, FRAME_CSP, demoSafeInputs, jsonForScript } from './frameTokens';

const SHADOWS = {
  none: 'none',
  sm: '0 1px 3px rgba(26, 25, 24, 0.08)',
  md: '0 4px 14px rgba(26, 25, 24, 0.12)',
};

export default {
  name: 'WidgetHost',
  components: { D3Insight },
  props: {
    placement: { type: Object, required: true },
    widget: { type: Object, default: null },
    pageId: { type: Number, default: null },
    sessionId: { type: String, default: '' },
    filters: { type: Object, default: () => ({}) },
    width: { type: Number, default: 0 },
    height: { type: Number, default: 0 },
  },
  data() {
    return { inputs: {}, loading: false, ready: false, error: '', reloads: 0 };
  },
  computed: {
    config() {
      const filters = this.filters || {};
      const resolve = (v) => {
        if (Array.isArray(v)) return v.map(resolve);
        if (v && typeof v === 'object') {
          if (typeof v.$filter === 'string') {
            const picked = filters[v.$filter];
            return resolve(picked !== undefined && picked !== '' && picked in v ? v[picked] : v.default);
          }
          return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, resolve(x)]));
        }
        return v;
      };
      return resolve(this.placement.config || {});
    },
    stock() {
      return this.widget && this.widget.kind === 'stock' ? stockComponent(this.widget.component) : null;
    },
    showChrome() {
      return !!this.config.title;
    },
    look() {
      return this.placement.style || {};
    },
    pad() {
      return Number(this.look.padding) || 0;
    },
    frameStyle() {
      const look = this.look;
      const style = {};
      if (look.background) style.background = look.background;
      if (look.border_color) style.borderColor = look.border_color;
      if (look.border_width != null) style.borderWidth = look.border_width + 'px';
      if (look.border_radius != null) style.borderRadius = look.border_radius + 'px';
      if (look.shadow) style.boxShadow = SHADOWS[look.shadow] || 'none';
      return style;
    },
    bodyWidth() {
      return Math.max(0, this.width - 2 * this.pad);
    },
    bodyHeight() {
      return Math.max(0, this.height - (this.showChrome ? 34 : 0) - 2 * this.pad);
    },
    inputNames() {
      return Object.keys(this.placement.inputs || {});
    },
    inputRows() {
      const out = {};
      this.inputNames.forEach((name) => { out[name] = (this.inputs[name] || {}).rows || []; });
      return out;
    },
    htmlDocument() {
      const template = (this.widget && this.widget.definition && this.widget.definition.template) || '';
      const inputs = demoSafeInputs(this.inputRows);
      return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">
<style>${TOKENS_CSS}
html,body{margin:0;max-width:100%;overflow-x:hidden}
body{font:14px/1.45 -apple-system,system-ui,sans-serif;color:var(--body);background:var(--card)}</style>
</head><body>
<div id="root">${template}</div>
<script>
window.inputs = ${jsonForScript(inputs)};
window.config = ${jsonForScript(this.config)};
document.dispatchEvent(new Event('kairon:ready'));
<\/script>
</body></html>`;
    },
  },
  watch: {
    filters: { deep: true, handler() { this.load(); } },
    'placement.inputs': { deep: true, handler() { this.load(); } },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      const names = this.inputNames;
      if (!names.length) {
        this.inputs = {};
        this.ready = true;
        return;
      }
      this.loading = true;
      this.error = '';
      const token = (this.loadToken = (this.loadToken || 0) + 1);
      try {
        const results = await Promise.all(names.map((name) => (this.pageId
          ? axios.post(`/api/impact_analytics/agent-pages/${this.pageId}/rows/`,
            { placement: this.placement.id, input: name, params: this.filters })
          : axios.post(`/api/impact_analytics/agent-sessions/${this.sessionId}/rows/`, {
            source: name, kind: (this.placement.inputs[name] || {}).kind, id: (this.placement.inputs[name] || {}).id,
            params: { ...((this.placement.inputs[name] || {}).params || {}), ...this.filters },
          })
        ).then(({ data }) => [name, { rows: data.rows || [], columns: data.columns || [], chart: data.chart || null }])
          .catch((err) => [name, { rows: [], columns: [], error: err?.response?.data?.error || 'Rows failed to load.' }])));
        if (token !== this.loadToken) return;
        const inputs = {};
        results.forEach(([name, value]) => { inputs[name] = value; });
        this.inputs = inputs;
        const failed = results.find(([, v]) => v.error);
        this.error = failed ? `${failed[0]}: ${failed[1].error}` : '';
        this.ready = true;
        this.reloads += 1;
      } finally {
        if (token === this.loadToken) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.wh { width: 100%; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; background: #fff; border: 1px solid #e6e4df; border-radius: 8px; overflow: hidden; }
.wh-head { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; height: 34px; border-bottom: 1px solid #f0efec; }
.wh-title { font-size: 12.5px; font-weight: 600; color: #1a1918; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wh-body { flex: 1; min-height: 0; position: relative; box-sizing: border-box; }
.wh-frame { width: 100%; height: 100%; border: 0; display: block; }
.wh-error { padding: 12px; font-size: 12px; color: #c23a28; }
.wh-loading { padding: 12px; font-size: 12px; color: #8b877f; }
.wh-spin { width: 10px; height: 10px; border: 2px solid #e6e4df; border-top-color: #EF6C3B; border-radius: 50%; animation: wh-spin 0.8s linear infinite; }
@keyframes wh-spin { to { transform: rotate(360deg); } }
</style>
